import { defineComponent as _defineComponent } from 'vue'
import { PaymentMethod } from "@/models/Subscriptions/Payment/PaymentMethod";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";
import { PaymentMethodType } from "@/models/Subscriptions/Payment/PaymentMethodType";
import SubscriptionRepository from "@/utils/API/SubscriptionRepository";
import { BraintreeCountries } from "@/utils/BraintreeCountries";
import CreatePaymentMethodForm from "@/components/forms/CreatePaymentMethodForm.vue";
import {computed, onMounted, ref, watch } from "vue";
import {useTranslations} from "@/hooks/useTranslations";
import {useNotify} from "@/hooks/useNotify";
import {useStore} from "@/hooks/useStore";
import VPreLoader from "@/components/vPreLoader.vue";
import VModal from "@/components/vModal.vue";
import {CreditCardDetails} from "@/models/PaymentMethods/CreditCardDetails";
import VTelInput from "@/components/vTelInput.vue";

type vPaymentMethodProps = {
  paymentMethodToEdit: number;
}

type vPaymentMethodExpose = {
  isAddingNewPaymentMethod: boolean,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'vPaymentMethods',
  props: {
    paymentMethodToEdit: null
  },
  emits: ["chosen", "creditCardToEdit", "addingNewPaymentMethod"],
  setup(__props: any, { expose, emit }) {

const props = __props as {
  paymentMethodToEdit: number;
};

expose<vPaymentMethodExpose>();




const chosenPaymentMethodId = ref(-1);
const paymentMethods = ref<PaymentMethod[]>([]);
const isLoading = ref(false);
const creditCardToEditCopy = ref<PaymentMethod>();
const isPaymentMethodChangeConfirmationDialogShown = ref(false);
const attemptedPaymentMethodId = ref(-1);
const isAddingNewPaymentMethod = ref(false);

const translations = useTranslations();
const notify = useNotify();
const store = useStore();

watch(chosenPaymentMethodId, (newValue) => {
  emit("chosen", newValue);
});

onMounted(async() => {
  await store.value.dispatch(TRANSLATIONS_FETCH, {
    category: "pages",
    collections: [
      "common",
      "managesubscription",
      "packages",
      "addons",
      "managePayment",
      "profile",
    ],
  });
  await loadPaymentMethods();

  if (paymentMethods.value.length === 0) {
    setIsAddingPaymentMethod();
  }
});

const creditCards = computed(() => {
  return paymentMethods.value.filter(
    (x: PaymentMethod) => x.paymentMethodType == PaymentMethodType.CreditCard
  );
});

const payPalAccounts = computed(() => {
  return paymentMethods.value.filter(
    (x: PaymentMethod) => x.paymentMethodType == PaymentMethodType.PayPal
  );
});

function paymentMethodEdit(cardId: number) {
  return props.paymentMethodToEdit === cardId;
}

function setIsAddingPaymentMethod() {
  isAddingNewPaymentMethod.value = true;
  emit("addingNewPaymentMethod", true);
}

function unsetIsAddingPaymentMethod() {
  isAddingNewPaymentMethod.value = false;
  emit("addingNewPaymentMethod", false);
}

async function handleCreatePaymentMethod() {
  await loadPaymentMethods();
  unsetIsAddingPaymentMethod();
}

//set chosen payment method
function chosenPaymentMethod(paymentMethodId: number) {
  if (chosenPaymentMethodId.value != paymentMethodId) {
    isPaymentMethodChangeConfirmationDialogShown.value = true;
    attemptedPaymentMethodId.value = paymentMethodId;
  }
}

function chosenPaymentMethodConfirmed() {
  chosenPaymentMethodId.value = attemptedPaymentMethodId.value;
  attemptedPaymentMethodId.value = -1;
  isPaymentMethodChangeConfirmationDialogShown.value = false;
}

//choose which payment method to edit
function editPaymentMethod(cardId: number) {
  emit("creditCardToEdit", cardId);

  //When editing the credit card, make a copy so if the user cancels, the card billing address will return to the previous state
  const creditCard = creditCards.value.find((x) => x.id === cardId);

  if (creditCard != null) {
    // @ts-ignore Types are valid but OR types don't play well with vue
    creditCardToEditCopy.value = {
      ...creditCard,
      billingAddress: { ...creditCard.billingAddress },
      details: { ...creditCard.details },
    };
  }
}

//Update the edited credit card
async function updateCreditCard(creditCard: PaymentMethod) {
  const creditCardDetails = creditCard.details as CreditCardDetails;

  const updatePaymentMethod = {
    Id: creditCard.id,
    CardholderName: creditCardDetails.cardholderName,
    ExpiryMonth: creditCardDetails.expiryMonth,
    ExpiryYear: creditCardDetails.expiryYear,
    BillingAddress: {
      Country: creditCard.billingAddress.country,
      State: creditCard.billingAddress.state,
      PostalCode: creditCard.billingAddress.postalCode,
      City: creditCard.billingAddress.city,
      AddressLine: creditCard.billingAddress.addressLine,
      PhoneNumber: creditCard.billingAddress.phoneNumber,
      FirstName: creditCard.billingAddress.firstName,
      LastName: creditCard.billingAddress.lastName,
    },
  };

  try {
    await SubscriptionRepository.updatePaymentMethod(updatePaymentMethod);
    notify.success("Successfully updated.");
    editPaymentMethod(-1);
    await loadPaymentMethods();
  }

  catch (error) {
    if (error.response) {
      notify.error(error.response.errorMessages[0].userVisibleMessage);
    }

    else {
      notify.error(translations.value.get("network-error"));
    }
  }
}

//Cancel the edit
function cancelEditPaymentMethod(cardId: number) {
  const editedCard = paymentMethods.value.find((x) => x.id === cardId);

  // On cancel return the billing address to the previous state
  if (editedCard != null) {
    editedCard.billingAddress = creditCardToEditCopy.value.billingAddress;
    editedCard.details = creditCardToEditCopy.value.details;
  }

  editPaymentMethod(-1);
}

// Get next 10 years from today's date
function formatedYears() {
  const years = [];
  const todaysDate = new Date();
  let year = todaysDate.getFullYear();
  const lastYear = year + 11;

  for (year; year < lastYear; year++) {
    years.push(year);
  }

  return years;
}

async function loadPaymentMethods() {
  isLoading.value = true;

  try {
    const response = await SubscriptionRepository.listPaymentMethods();
    paymentMethods.value = response.data.result;

    const defaultPaymentMethod = paymentMethods.value.find((x) => x.isDefault);

    if (defaultPaymentMethod) {
      chosenPaymentMethodId.value = defaultPaymentMethod.id;
      emit("chosen", defaultPaymentMethod.id);
    }

    else if (paymentMethods.value.length > 0) {
      chosenPaymentMethodId.value = paymentMethods.value[0].id;
    }
  }

  finally {
    isLoading.value = false;
  }
}

return { __sfc: true,props, emit, chosenPaymentMethodId, paymentMethods, isLoading, creditCardToEditCopy, isPaymentMethodChangeConfirmationDialogShown, attemptedPaymentMethodId, isAddingNewPaymentMethod, translations, notify, store, creditCards, payPalAccounts, paymentMethodEdit, setIsAddingPaymentMethod, unsetIsAddingPaymentMethod, handleCreatePaymentMethod, chosenPaymentMethod, chosenPaymentMethodConfirmed, editPaymentMethod, updateCreditCard, cancelEditPaymentMethod, formatedYears, loadPaymentMethods, BraintreeCountries, CreatePaymentMethodForm, VPreLoader, VModal, VTelInput }
}

})